"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function DailyNourishmentDelivered() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imgRef,
    isVisible: isImgVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="228536" id="the-digital-atelier" className="relative w-full min-h-[100svh] flex flex-col lg:flex-row overflow-hidden bg-background text-foreground">
            {}
            <div className="w-full lg:w-[60%] bg-background relative z-10 flex items-center px-6 py-24 sm:px-12 md:py-32 lg:px-20 xl:px-32">
                {}
                <div className="absolute inset-0 opacity-10 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/graphy.png')] invert" />
                
                <div ref={textRef} className={cn("max-w-3xl transition-all duration-1000 ease-out", isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
                    <h1 className="font-default text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-foreground leading-[1.1] tracking-tighter">
                        Precision in<br />
                        <span className="bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent">Every Facet</span>
                    </h1>
                    <p className="mt-8 text-lg sm:text-xl md:text-2xl text-muted-foreground font-default max-w-2xl leading-relaxed">
                        Transforming concepts into high-fidelity jewelry CAD designs. From .3dm and .stl files to photorealistic .jpg renderings, we define the future of digital craftsmanship.
                    </p>
                    <div className="mt-12 flex flex-wrap gap-4">
                        <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 h-14 px-8 rounded-md text-lg font-semibold transition-all duration-300 hover:px-10 shadow-lg group">
                            <Link to="/StartYourProjectPage">
                                Get a Quote
                                <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </Button>
                    </div>
                </div>
            </div>

            {}
            <div className="w-full lg:w-[40%] bg-accent/20 relative min-h-[50vh] lg:min-h-full flex items-center justify-center z-20">
                {}
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-secondary/10 mix-blend-overlay pointer-events-none" />

                <div ref={imgRef} className={cn("relative w-[90%] max-w-[600px] transition-all duration-1000 delay-300 ease-out", isImgVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-24 scale-95")}>
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1670658/img/ladies_ring_pave_setting.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1670658/img/ladies_ring_pave_setting.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1670658/img/ladies_ring_pave_setting.png-wvc-sizes" alt="Beautiful ladies ring with pavé setting" className="w-full h-auto rounded-lg shadow-2xl object-cover" loading="lazy" decoding="async" />
                </div>

                {}
                <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50">
                    <div className="w-[1px] h-16 bg-primary relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-4 bg-secondary animate-[bounce_2s_infinite]" />
                    </div>
                </div>
            </div>
        </section>;
}